import { useState, useEffect } from "react";
import { useSiteContent } from "@/context/SiteContentContext";
import {
    RefreshCw,
    Check,
    Receipt,
} from "lucide-react";

// ---------- Content Tab (editable site strings) ----------
export default function ContentTab() {
    const { content, updateContent, defaults, refresh } = useSiteContent();
    const [form, setForm] = useState(content);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [err, setErr] = useState("");

    useEffect(() => setForm(content), [content]);

    const save = async () => {
        setSaving(true);
        setErr("");
        setSaved(false);
        try {
            await updateContent(form);
            setSaved(true);
            setTimeout(() => setSaved(false), 2500);
        } catch (e) {
            setErr(e.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const reset = () => setForm(defaults);

    const fields = [
        { group: "Brand", items: [
            { key: "brand_name", label: "Brand name (navbar)", type: "text" },
            { key: "brand_short", label: "Brand short (logo square)", type: "text" },
        ]},
        { group: "Top banner", items: [
            { key: "top_banner", label: "Left text", type: "text" },
            { key: "top_banner_right", label: "Right text", type: "text" },
        ]},
        { group: "Home hero", items: [
            { key: "hero_kicker", label: "Kicker", type: "text" },
            { key: "hero_line1", label: "Headline line 1", type: "text" },
            { key: "hero_line2", label: "Headline line 2 (accent color)", type: "text" },
            { key: "hero_line3", label: "Headline line 3", type: "text" },
            { key: "hero_subtitle", label: "Subtitle", type: "textarea" },
            { key: "hero_primary_cta", label: "Primary button text", type: "text" },
            { key: "hero_secondary_cta", label: "Secondary button text", type: "text" },
        ]},
        { group: "Login page", items: [
            { key: "login_title", label: "Title", type: "text" },
            { key: "login_hero_line1", label: "Hero line 1", type: "text" },
            { key: "login_hero_line2", label: "Hero line 2 (accent)", type: "text" },
            { key: "login_hero_line3", label: "Hero line 3", type: "text" },
            { key: "login_hero_subtitle", label: "Hero subtitle", type: "textarea" },
        ]},
        { group: "Footer", items: [
            { key: "footer_tagline", label: "Footer tagline", type: "textarea" },
            { key: "footer_copyright", label: "Bottom copyright line", type: "text" },
        ]},
        { group: "Contact & location", items: [
            { key: "contact_address", label: "Street address", type: "text" },
            { key: "contact_city", label: "City / postal code", type: "text" },
            { key: "contact_phone", label: "Phone", type: "text" },
            { key: "contact_email", label: "Email", type: "text" },
            { key: "contact_hours", label: "Opening hours", type: "textarea" },
            { key: "map_lat", label: "Map latitude (e.g. 41.3275)", type: "number" },
            { key: "map_lng", label: "Map longitude (e.g. 19.8187)", type: "number" },
            { key: "map_zoom", label: "Map zoom (3–20, default 15)", type: "number" },
        ]},
        { group: "Currency", items: [
            { key: "currency_lek_per_eur", label: "Exchange rate — Lek per 1 EUR (e.g. 100)", type: "number" },
        ]},
        { group: "POS Receipt (80mm thermal — OCPP-80T)", items: [
            { key: "business_name", label: "Business name (shown on receipts; falls back to hero title)", type: "text" },
            { key: "business_nipt", label: "NIPT", type: "text" },
            { key: "website_url", label: "Website (shown in footer, e.g. turbocimi.com)", type: "text" },
            { key: "receipt_footer", label: "Receipt footer text (default: Faleminderit për blerjen!)", type: "text" },
            { key: "warranty_text", label: "Warranty text (default: Garanci 12 muaj sipas kushteve.)", type: "text" },
        ]},
        { group: "Navbar appearance", items: [
            { key: "nav_font_size", label: "Nav link size (px) — 10 to 24", type: "number" },
            { key: "nav_font_weight", label: "Nav weight (normal / bold / black)", type: "text" },
            { key: "nav_uppercase", label: "UPPERCASE nav labels", type: "bool" },
        ]},
    ];

    return (
        <div data-testid="admin-content-tab">
            <div className="flex items-center justify-between mb-4">
                <p className="text-sm text-slate-600">
                    Edit the text that appears across the site. Changes apply to all visitors.
                </p>
                <div className="flex gap-2">
                    <button onClick={reset} className="btn-outline">Reset to defaults</button>
                    <button onClick={refresh} className="btn-outline"><RefreshCw className="w-3.5 h-3.5" /> Reload</button>
                    <button onClick={save} disabled={saving} className="btn-primary" data-testid="save-content-button">
                        <Check className="w-4 h-4" /> {saving ? "Saving..." : "Save changes"}
                    </button>
                </div>
            </div>

            {saved && (
                <div className="mb-4 border border-emerald-200 bg-emerald-50 text-emerald-800 px-4 py-2 text-sm font-mono">
                    ✓ Content saved. Refresh any open pages to see changes.
                </div>
            )}
            {err && (
                <div className="mb-4 border border-red-200 bg-red-50 text-red-800 px-4 py-2 text-sm font-mono">
                    {err}
                </div>
            )}

            <div className="space-y-6 max-w-3xl">
                {fields.map((group) => (
                    <div key={group.group} className="border border-slate-200">
                        <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 label-kicker text-slate-600">
                            {group.group}
                        </div>
                        <div className="p-4 space-y-3">
                            {group.items.map((f) => (
                                <div key={f.key}>
                                    <label className="label-kicker mb-1 block">{f.label}</label>
                                    {f.type === "textarea" ? (
                                        <textarea
                                            rows={2}
                                            value={form[f.key] || ""}
                                            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                            className="input-field"
                                            data-testid={`content-${f.key}`}
                                        />
                                    ) : f.type === "number" ? (
                                        <input
                                            type="number"
                                            step="any"
                                            value={form[f.key] ?? ""}
                                            onChange={(e) => setForm({ ...form, [f.key]: e.target.value === "" ? null : parseFloat(e.target.value) })}
                                            className="input-field"
                                            data-testid={`content-${f.key}`}
                                        />
                                    ) : f.type === "bool" ? (
                                        <label className="flex items-center gap-2 text-sm">
                                            <input
                                                type="checkbox"
                                                checked={!!form[f.key]}
                                                onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                                                className="w-4 h-4 accent-[hsl(var(--primary))]"
                                                data-testid={`content-${f.key}`}
                                            />
                                            <span className="text-slate-600">{form[f.key] ? "On" : "Off"}</span>
                                        </label>
                                    ) : (
                                        <input
                                            type="text"
                                            value={form[f.key] || ""}
                                            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                                            className="input-field"
                                            data-testid={`content-${f.key}`}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                {/* USP strip editor */}
                <UspEditor items={form.usp_items || []} onChange={(items) => setForm({ ...form, usp_items: items })} />
            </div>
        </div>
    );
}

const USP_ICONS = ["package", "zap", "shield", "truck", "check", "star", "target", "rocket"];

function UspEditor({ items, onChange }) {
    const update = (i, patch) => {
        const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
        onChange(next);
    };
    const add = () => {
        onChange([...items, { label: "New benefit", sub: "Subtitle", icon: "package", enabled: true }]);
    };
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
    const move = (i, dir) => {
        const next = [...items];
        const j = i + dir;
        if (j < 0 || j >= next.length) return;
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };

    return (
        <div className="border border-slate-200" data-testid="usp-editor">
            <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 label-kicker text-slate-600 flex items-center justify-between">
                <span>USP strip (home page)</span>
                <button type="button" onClick={add} className="text-xs font-mono text-[hsl(var(--primary))]" data-testid="usp-add">
                    + Add item
                </button>
            </div>
            <div className="p-4 space-y-2">
                {items.length === 0 && (
                    <div className="text-xs font-mono text-slate-400">No items yet. Click "+ Add item".</div>
                )}
                {items.map((it, i) => (
                    <div key={i} className="grid grid-cols-[auto_auto_1fr_1fr_auto_auto] gap-2 items-center border border-slate-200 p-2" data-testid={`usp-row-${i}`}>
                        <div className="flex flex-col">
                            <button type="button" onClick={() => move(i, -1)} className="text-xs px-1" title="Up">▲</button>
                            <button type="button" onClick={() => move(i, 1)} className="text-xs px-1" title="Down">▼</button>
                        </div>
                        <select
                            value={it.icon || "package"}
                            onChange={(e) => update(i, { icon: e.target.value })}
                            className="input-field !py-1 !px-2 !text-xs !w-auto"
                            data-testid={`usp-icon-${i}`}
                        >
                            {USP_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                        </select>
                        <input
                            value={it.label || ""}
                            onChange={(e) => update(i, { label: e.target.value })}
                            placeholder="Label"
                            className="input-field !py-1 !px-2 !text-xs"
                            data-testid={`usp-label-${i}`}
                        />
                        <input
                            value={it.sub || ""}
                            onChange={(e) => update(i, { sub: e.target.value })}
                            placeholder="Sub-label"
                            className="input-field !py-1 !px-2 !text-xs"
                            data-testid={`usp-sub-${i}`}
                        />
                        <label className="flex items-center gap-1 text-xs font-mono" title="Enabled">
                            <input
                                type="checkbox"
                                checked={it.enabled !== false}
                                onChange={(e) => update(i, { enabled: e.target.checked })}
                                className="w-4 h-4 accent-[hsl(var(--primary))]"
                                data-testid={`usp-enabled-${i}`}
                            />
                            ON
                        </label>
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            className="text-red-600 text-xs"
                            title="Remove"
                            data-testid={`usp-remove-${i}`}
                        >
                            ✕
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

